import {
  contactsCreateMutation,
  contactsListQueryKey,
  leadsContactsListQueryKey,
  leadsContractsListQueryKey,
  leadsInvoiceContactsRetrieveQueryKey
} from '@crm/api'
import { DuplicateAlert } from '@crm/components'
import { handleContactMutationError, withValues } from '@crm/utils'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { useMutation } from '@tanstack/react-query'
import { Col, Form, Input, Modal, notification, Row } from 'antd'
import { ModalProps } from 'antd/lib'

const formName = 'add-contact-form'

type Props = {
  leadId?: number
  open: boolean
  onClose: () => void
} & ModalProps

export const AddContactModal = ({ leadId, open, onClose, ...props }: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const { mutate, isPending, error, reset } = useMutation({
    ...contactsCreateMutation(),
    onError: (e) => handleContactMutationError(e, form),
    onSuccess: () => {
      invalidateQueries([
        contactsListQueryKey(),
        leadsContactsListQueryKey({ path: { lead_id: String(leadId) } }),
        leadsInvoiceContactsRetrieveQueryKey({ path: { id: Number(leadId) } }),
        leadsContractsListQueryKey({ path: { lead_id: String(leadId) } })
      ])

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
      form.resetFields()
      onClose()
    }
  })

  const onFormFinish = async () => {
    const values = await form.validateFields()
    mutate({ body: withValues({ ...values, lead: leadId }) })
  }

  return (
    <Modal
      open={open}
      maskClosable={false}
      title={t('buttons.add_contact')}
      cancelText={t('buttons.cancel')}
      onCancel={onClose}
      okText={t('buttons.save')}
      okButtonProps={{ htmlType: 'submit', form: formName, loading: isPending }}
      {...props}
    >
      <Form
        name={formName}
        form={form}
        onFinish={onFormFinish}
        layout="vertical"
      >
        <DuplicateAlert error={error} onClose={reset} />

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="first_name"
              label={t('contacts.first_name')}
              required
              rules={[
                {
                  required: true
                }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="last_name"
              label={t('contacts.last_name')}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="email"
              label={t('contacts.email')}
              rules={[{ type: 'email' }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="job_title" label={t('contacts.job_title')}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
